import type { Decision } from "../dashboard/deskLogic";
import { viewerNote as deskViewerNote } from "../dashboard/deskLogic";
import { byNumber, byText, reversed, type RegisterConfig } from "../register/registerLogic";

/**
 * The Director of Studies' dashboard: decisions waiting, each class's
 * teaching gaps and syllabus coverage, and the examinations pipeline.
 * Mirrors apps/dashboard/director_of_studies.py.
 */

export interface StudiesClass {
  id: number; name: string; level: string; class_teacher: string; learners: number
  /** Subjects the level's syllabi call for. */
  subjects: number
  /** Of those, the ones with no teacher assigned, and the ones with no lesson on the timetable. */
  unassigned: string[]; untimetabled: string[]
  lessons_per_week: number
  topics: number; topics_done: number; coverage: number | null; behind: number
}
export interface BehindRow { class_name: string; subject_name: string; behind: number; coverage: number; teachers: string[] }
export interface UpcomingExam { id: number; name: string; subject: string; date: string; start_time: string; classes: string[] }
export interface StudiesDesk {
  generated_at: string; today: string; viewer_role: string; academic_year: string
  decisions: Decision[]
  classes: StudiesClass[]
  behind: BehindRow[]
  exams: { stages: Record<"draft" | "submitted" | "moderated" | "approved" | "published", number>; upcoming: UpcomingExam[] }
  registers: { classes: number; marked: number }
}

type Tone = { label: string; dot: string };

/** Everything the class is missing, worst first. */
export function classStatus(row: StudiesClass): Tone {
  if (!row.subjects) return { label: "No syllabus for this level", dot: "bg-gray-400" };
  if (row.unassigned.length) return { label: `${row.unassigned.length} without a teacher`, dot: "bg-red-500" };
  if (row.untimetabled.length) return { label: `${row.untimetabled.length} not timetabled`, dot: "bg-amber-500" };
  if (!row.class_teacher) return { label: "No class teacher", dot: "bg-amber-500" };
  if (row.behind) return { label: `${row.behind} ${row.behind === 1 ? "topic" : "topics"} behind`, dot: "bg-sky-500" };
  return { label: "On track", dot: "bg-emerald-500" };
}

export const hasGap = (row: StudiesClass) => !row.class_teacher || row.unassigned.length > 0 || row.untimetabled.length > 0;
export const gapCount = (row: StudiesClass) => row.unassigned.length + row.untimetabled.length + (row.class_teacher ? 0 : 1);

export const classConfig: RegisterConfig<StudiesClass> = {
  search: (row) => `${row.name} ${row.level} ${row.class_teacher} ${row.unassigned.join(" ")} ${row.untimetabled.join(" ")}`,
  views: [
    { key: "all", label: "All" },
    { key: "gaps", label: "With gaps", match: hasGap },
    { key: "unassigned", label: "Subjects without a teacher", match: (row) => row.unassigned.length > 0 },
    { key: "untimetabled", label: "Not timetabled", match: (row) => row.untimetabled.length > 0 },
    { key: "behind", label: "Behind", match: (row) => row.behind > 0 },
  ],
  sorts: [
    { key: "school", label: "School order", compare: () => 0 },
    { key: "gaps", label: "Most gaps", compare: reversed(byNumber(gapCount)) },
    { key: "coverage", label: "Least covered", compare: byNumber((row) => row.coverage ?? 101) },
    { key: "name", label: "Class name", compare: byText((row) => row.name) },
  ],
};

/** The school-wide totals the tiles show. */
export function studiesTotals(rows: StudiesClass[]) {
  const topics = rows.reduce((sum, row) => sum + row.topics, 0);
  const done = rows.reduce((sum, row) => sum + row.topics_done, 0);
  return {
    unassigned: rows.reduce((sum, row) => sum + row.unassigned.length, 0),
    untimetabled: rows.reduce((sum, row) => sum + row.untimetabled.length, 0),
    noClassTeacher: rows.filter((row) => !row.class_teacher).length,
    behind: rows.reduce((sum, row) => sum + row.behind, 0),
    coverage: topics ? Math.round((done / topics) * 1000) / 10 : null,
    classesWithGaps: rows.filter(hasGap).length,
  };
}

export const viewerNote = (role: string) => deskViewerNote(role, "director_of_studies", "the Director of Studies' dashboard");
